import { Component, inject, input, OnInit } from '@angular/core';
import { EventType, HistoryVisibility, Visibility } from 'matrix-js-sdk';
import { Room } from '../../../../model/room';
import { RoomProperties } from '../../../../../shared-api/roomProperties';
import { SnackbarService } from '../../../../services/snackbar/snackbar.service';
import { DialogChangesService } from '../../../../services/dialog/dialog-changes.service';
import { RoomPowerLevelService } from '../../../../services/room-power-level/room-power-level.service';
import { RoomService } from '../../../../services/room/room.service';
import { MatrixService } from '../../../../services/matrix/matrix.service';
import { RoomListAccessService } from '../../../../services/room-list/room-list-access.service';
import { RoomHistoryVisibility } from '../../../../model/room-history-visibility';
import { RoomPowerLevel } from '../../../../model/room-power-level';
import { MatTooltip } from '@angular/material/tooltip';
import { SecuritySettingsTestIds } from './sb-security-settings.test-ids';
import { TooltipIconComponent } from '../../../../common/components/base-settings-dialog/tooltip-icon/tooltip-icon.component';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { ButtonComponent } from '../../../../common/components/button/button.component';
import { SettingsSliderComponent } from '../../../../common/components/base-settings-dialog/settings-slider/settings-slider.component';
import { MatChipListbox } from '@angular/material/chips';
import { ChipComponent } from '../../../core/chip/chip.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-sb-security-settings',
  imports: [
    TooltipIconComponent,
    MatRadioGroup,
    MatRadioButton,
    MatTooltip,
    ButtonComponent,
    SettingsSliderComponent,
    MatChipListbox,
    ChipComponent,
    MatCheckbox,
    FormsModule
  ],
  templateUrl: './sb-security-settings.component.html',
  styleUrl: './sb-security-settings.component.scss'
})
/**
 * Klasse notwendig für Sicherheitseinstellungen des Raumes. Erfüllt keine Afo.
 * A_25561, Hinweis auf unverschlüsselte Kommunikation
 * Begründung: Stand jetzt Designstory
 */
export class SbSecuritySettingsComponent implements OnInit {
  protected readonly testIds = SecuritySettingsTestIds;
  roomId = input<string>();
  public securitySettings: SecuritySettings | undefined;
  infoText =
    'Änderungen an der Sichtbarkeit des Verlaufs gelten nur für zukünftige Nachrichten. ' +
    'Die Sichtbarkeit des existierenden Verlaufs bleibt unverändert.';
  public room: Room | undefined;
  powerLevels: RoomPowerLevel | undefined;
  userId: string | undefined;
  protected readonly Visibility = Visibility;
  protected readonly EventType = EventType;
  protected readonly roomPowerLevelService = inject(RoomPowerLevelService);
  private readonly roomService = inject(RoomService);
  private readonly roomListAccessService = inject(RoomListAccessService);
  private readonly matrix = inject(MatrixService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly dialogChangesService = inject(DialogChangesService);
  private initialSettings!: SecuritySettings;

  ngOnInit(): void {
    if (!this.roomId()) {
      throw new Error('Raumeinstellungen können nicht ohne Raumid aufgerufen werden.');
    }

    this.roomListAccessService.getCurrentModelRoom$().subscribe(room => {
      this.room = room;
    });

    this.securitySettings = this.getSecuritySettings();
    this.initialSettings = { ...this.securitySettings };

    this.powerLevels = this.room?.powerLevels;
    this.userId = this.matrix.getUserId();
  }

  async onSave(): Promise<void> {
    let roomProperties: RoomProperties;
    if (this.room?.roomAccess === 'public') {
      roomProperties = {
        name: this.room.name,
        roomAccess: this.securitySettings?.visibility,
        isEncrypted: this.securitySettings?.encryption
      };
    } else {
      roomProperties = {
        name: this.room!.name
      };
    }
    if (this.room?.roomId && this.securitySettings) {
      await this.setRoomHistoryVisibility(this.securitySettings.history);
    }
    await this.roomService.changeRoomSettings(this.roomId(), roomProperties);
    this.snackbarService.success('Raumeinstellungen geändert');

    this.dialogChangesService.setChanges(false);
  }

  public changeAccess(visibility: Visibility): void {
    if (this.room!.roomAccess === 'private') {
      return;
    }
    if (visibility === this.securitySettings!.visibility) {
      return;
    }
    this.securitySettings!.visibility = visibility;
    if (visibility === Visibility.Private) {
      this.securitySettings!.encryption = true;
    }
    this.checkForChanges();
  }

  protected checkForChanges(): void {
    if (!this.securitySettings) return;

    this.dialogChangesService.setChanges(
      this.securitySettings.encryption !== this.room?.isEncrypted ||
        this.securitySettings.visibility !==
          (this.room?.roomAccess === 'public' ? Visibility.Public : Visibility.Private) ||
        this.securitySettings.history !== this.room.historyVisibility
    );
  }

  private async setRoomHistoryVisibility(historyVisibility: RoomHistoryVisibility): Promise<void> {
    if (historyVisibility) {
      if (this.room && this.room.historyVisibility !== historyVisibility) {
        await this.matrix.client.sendStateEvent(this.room.roomId, EventType.RoomHistoryVisibility, {
          history_visibility: historyVisibility.valueOf() as HistoryVisibility
        });
      }
    }
  }

  private getSecuritySettings(): SecuritySettings {
    if (!this.room) {
      throw new Error('No Room found');
    }
    if (this.room.isEncrypted === undefined) {
      throw new Error('no Encryption status');
    }
    if (!this.room.historyVisibility) {
      throw new Error('no Historyvisibility');
    }
    return {
      encryption: this.room.isEncrypted,
      visibility: this.room.roomAccess === 'public' ? Visibility.Public : Visibility.Private,
      history: this.room.historyVisibility
    };
  }
}

export interface SecuritySettings {
  encryption: boolean;
  visibility: Visibility;
  history: RoomHistoryVisibility;
}
